/**
 * Lịch sự kiện khách hàng — định nghĩa cột.
 *
 * Không hiển thị năm: sự kiện lặp hằng năm, `event_date` chỉ giữ năm gốc để tính ngày/tháng.
 */
import type { TFunction } from 'i18next'
import dayjs from 'dayjs'
import { Link } from 'react-router-dom'
import type { DataTableColumn } from '@/components/ui/data-table-customized'
import type { CustomerCalendarEvent } from '@/modules/Customer/types'
import { generatePathDatacentral, paths } from '@/utils/path'

const EMPTY = (text: string) => (
    <span className="italic text-gray-400">{text}</span>
)

const joinOrEmpty = (values: string[] | null, emptyText: string) => {
    const text = values?.filter(Boolean).join(', ')
    if (!text) return EMPTY(emptyText)
    return (
        <span className="block truncate" title={text}>
            {text}
        </span>
    )
}

export const getCustomerEventColumns = (
    t: TFunction,
    /** employee_id -> tên. Thiếu id nào thì hiển thị "#<id>" thay vì bỏ trống. */
    employeeNames: Record<number, string>
): DataTableColumn<CustomerCalendarEvent>[] => [
    {
        key: 'event_date',
        title: t('calendar_events.column_date'),
        width: 100,
        render: (_value, row) => (
            <span className="font-medium tabular-nums">
                {dayjs(row.event_date).format('DD/MM')}
            </span>
        ),
    },
    {
        key: 'event_type',
        title: t('calendar_events.column_type'),
        width: 150,
        render: (_value, row) => (
            <span
                className={
                    row.event_type === 'birthday'
                        ? 'rounded bg-pink-50 px-2 py-0.5 text-pink-700'
                        : 'rounded bg-blue-50 px-2 py-0.5 text-blue-700'
                }
            >
                {row.event_type === 'birthday'
                    ? t('calendar_events.type_birthday')
                    : t('calendar_events.type_founding')}
            </span>
        ),
    },
    {
        key: 'customer_name',
        title: t('calendar_events.column_customer'),
        width: 240,
        render: (_value, row) => (
            <Link
                to={generatePathDatacentral(paths.customerDetail, {
                    id: row.customer_id,
                })}
                className="uppercase text-primary hover:underline"
            >
                {row.customer_name}
            </Link>
        ),
    },
    {
        key: 'company_name',
        title: t('calendar_events.column_company'),
        width: 240,
        render: (_value, row) =>
            row.company_name ? (
                <span className="block truncate" title={row.company_name}>
                    {row.company_name}
                </span>
            ) : (
                EMPTY(t('calendar_events.no_company'))
            ),
    },
    {
        key: 'contact_names',
        title: t('calendar_events.column_contacts'),
        width: 260,
        render: (_value, row) =>
            joinOrEmpty(row.contact_names, t('calendar_events.no_contact')),
    },
    {
        key: 'followers',
        title: t('calendar_events.column_followers'),
        width: 240,
        render: (_value, row) =>
            joinOrEmpty(
                row.followers?.map(
                    (id) => employeeNames[id] ?? `#${id}`
                ) as string[],
                t('calendar_events.no_follower')
            ),
    },
]
